import { useMemo, useRef, useState } from 'react';
import { merge, values } from 'lodash';
import ReportingDataTable from '../data-table/ReportingDataTable';
import useStyles from './MatrixStyles';
import buildGridConfig, { defaultColumnOverrides } from './MatrixHelper';
import { ROWSIZE } from '../data-table/DataTableConstants';
import { convertLoadParamsToQueryParams, convertQueryResponse } from '../data-table/Loaders';
import { useDataService } from '../../services/ApiDataService';
import { ASCENDING, DESCENDING } from './Sorter';
import { useFilterState } from '../../stores/FilterStateStore';
import { usePageFilterState } from '../page-framework/panel/Panel';

function mergeMatrixParams(filterState, dataSourceParams, loaderParams) {
    const filterParams = values(filterState?.parameters);
    const loadParams = convertLoadParamsToQueryParams(loaderParams);
    return merge({}, dataSourceParams, loadParams, ...filterParams);
}

function getInitialSorters(dataSource) {
    const regex = /(-?)(.*)/;
    const sort = dataSource?.params?.sort;
    if (!sort || sort.indexOf(',') < 0) {
        return [];
    }

    return sort.split(',').map((sortStr) => {
        const matches = regex.exec(sortStr);
        const [, /* Not used */ direction, property] = matches;
        return {
            property,
            direction: direction === '-' ? DESCENDING : ASCENDING,
        };
    });
}

export default function Matrix(props) {
    const { visibleRows, dataSource } = props;
    const classes = useStyles();
    const [filterState] = useFilterState();
    const [pageFilterState] = usePageFilterState();
    const [columns, setColumns] = useState([]);
    const sorters = useMemo(() => getInitialSorters(dataSource), [dataSource]);
    const dataService = useDataService();
    const updateColumns = useRef();

    updateColumns.current = (response) => {
        const { params: dataSourceParams } = dataSource;
        setColumns(buildGridConfig(response, dataSourceParams, pageFilterState, classes));
    };

    const loader = useMemo(() => {
        // you must have filterstate and a datasource for the query loader to work...
        if (!filterState || !filterState.initialized || !dataSource) return null;

        return (loaderParams) => {
            const { url, params: dataSourceParams } = dataSource;
            const queryParams = mergeMatrixParams(filterState, dataSourceParams, loaderParams);

            return dataService
                .get(url, queryParams)
                .then((response) => {
                    updateColumns.current(response);
                    return response;
                })
                .then((response) => convertQueryResponse(dataSource, response));
        };
    }, [filterState, dataSource, dataService]);

    const tableProps = useMemo(
        () => ({
            disableCSVExport: true,
            disableColumnRemover: !!props?.disableColumnRemover,
            panelProps: props,
            sorters,
            loader,
            columns,
            visibleRows,
            rowSize: ROWSIZE.SMALL,
            className: classes.matrixContainer,
            columnOverrides: defaultColumnOverrides(props?.dataSource?.params || {}),
        }),
        [props, sorters, loader, columns, visibleRows, classes]
    );

    if (!columns) return null;

    return <ReportingDataTable {...tableProps} />;
}

Matrix.defaultProps = {
    handleSearchChange: () => {},
    visibleRows: 0,
};
